import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { AlertTriangle, LayoutTemplate } from 'lucide-react';
import api from '../../services/api';
import { useWorkflowStore } from '../../hooks/useWorkflowStore';

export default function LoadTemplateModal({ campaignId, campaignVertical, onClose, onApplied }) {
  const applyTemplate = useWorkflowStore((state) => state.applyTemplate);
  const loadDraft = useWorkflowStore((state) => state.loadDraft);

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAllVerticals, setShowAllVerticals] = useState(!campaignVertical);
  const [applyingId, setApplyingId] = useState(null);
  // Set once a template's apply attempt comes back 422 (vertical mismatch) -
  // surfaces a confirm-to-force prompt instead of silently retrying with
  // override_vertical_mismatch: true.
  const [mismatchFor, setMismatchFor] = useState(null);

  const load = () => {
    setLoading(true);
    setError('');
    const params = {
      limit: 100, page_size: 100, per_page: 100,
      ...(showAllVerticals || !campaignVertical ? {} : { vertical: campaignVertical }),
    };
    api.get('/flow-templates/', { params })
      .then((res) => setTemplates(res.data?.items || res.data || []))
      .catch((err) => {
        setError(
          err.response?.status === 403
            ? "You don't have permission to view flow templates."
            : err.response?.data?.detail || 'Failed to load templates.'
        );
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAllVerticals]);

  const handleApply = async (template, override = false) => {
    // applyTemplate reloads the draft from the backend on success, which
    // overwrites whatever's on the canvas right now - any local edits not
    // yet saved would vanish silently otherwise, since draftHash only
    // tracks the persisted draft, not in-canvas dirty state.
    if (!override && !window.confirm(`Load "${template.name}"? This replaces the current draft - any unsaved canvas edits will be lost.`)) {
      return;
    }
    setApplyingId(template.id);
    try {
      await applyTemplate(campaignId, template.id, override);
      toast.success(`Applied template "${template.name}" to the draft.`);
      setMismatchFor(null);
      onApplied?.();
      onClose();
    } catch (err) {
      if (err.response?.status === 422) {
        setMismatchFor(template);
      } else if (err.response?.status === 409) {
        toast.error('Draft conflict: another session updated this flow. Reloading...');
        await loadDraft(campaignId);
        onClose();
      } else if (err.response?.status === 403) {
        toast.error("You don't have permission to apply flow templates.");
      } else {
        toast.error(err.response?.data?.detail || 'Failed to apply template');
      }
    } finally {
      setApplyingId(null);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560 }}>
        <div className="modal-header">
          <h2 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <LayoutTemplate size={18} /> Load from Template
          </h2>
          <button className="btn btn-ghost btn-icon btn-sm" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {campaignVertical && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, marginBottom: 14 }}>
              <input
                type="checkbox"
                checked={showAllVerticals}
                onChange={(e) => setShowAllVerticals(e.target.checked)}
              />
              Show templates from all verticals (currently filtered to "{campaignVertical}")
            </label>
          )}

          {loading && <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)' }}>Loading templates…</div>}
          {!loading && error && <div className="partner-modal-error">{error}</div>}
          {!loading && !error && templates.length === 0 && (
            <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
              No templates found{showAllVerticals ? '' : ' for this vertical'}.
            </div>
          )}

          {!loading && !error && templates.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxHeight: 380, overflowY: 'auto' }}>
              {templates.map((t) => (
                <div
                  key={t.id}
                  style={{
                    padding: 12,
                    borderRadius: 8,
                    background: 'var(--bg-raised)',
                    border: '1px solid var(--border)',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700 }}>{t.name}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                        Vertical: <strong>{t.vertical}</strong>
                        {t.description ? ` · ${t.description}` : ''}
                      </div>
                    </div>
                    <button
                      className="btn btn-secondary btn-sm"
                      disabled={applyingId === t.id}
                      onClick={() => handleApply(t)}
                    >
                      {applyingId === t.id ? 'Applying…' : 'Apply'}
                    </button>
                  </div>

                  {mismatchFor?.id === t.id && (
                    <div
                      style={{
                        marginTop: 10,
                        padding: 10,
                        borderRadius: 6,
                        background: 'var(--amber-bg, rgba(245, 158, 11, 0.12))',
                        border: '1px solid var(--amber, #f59e0b)',
                        fontSize: 12,
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 700, marginBottom: 6 }}>
                        <AlertTriangle size={14} /> Vertical mismatch
                      </div>
                      This template is built for <strong>{t.vertical}</strong>, but this campaign's vertical is{' '}
                      <strong>{campaignVertical || 'different'}</strong>. Applying it anyway will be logged as an
                      audit event.
                      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                        <button className="btn btn-ghost btn-sm" onClick={() => setMismatchFor(null)}>Cancel</button>
                        <button
                          className="btn btn-primary btn-sm"
                          disabled={applyingId === t.id}
                          onClick={() => handleApply(t, true)}
                        >
                          Apply anyway
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
